import type {
  AdminBookingSortDirection,
  AdminBookingSortField,
  AdminBookingStatus,
} from '@/api/core/bookings';
import { hasAsciiControlOrSpace, isOffsetDateTime, isUuid } from '@/lib/validation';

const STATUSES: readonly AdminBookingStatus[] = ['PENDING_PAYMENT', 'CONFIRMED', 'CANCELLED'];
const canonicalUuid = (value: string | null): value is string =>
  value !== null && isUuid(value) && value === value.toLowerCase();
const SORT_FIELDS: readonly AdminBookingSortField[] = [
  'created_at',
  'start_at',
  'end_at',
  'status',
  'booking_id',
];
const LIST_KEYS = [
  'property_id',
  'status',
  'starts_from',
  'starts_before',
  'sort_by',
  'sort_direction',
  'cursor',
] as const;

export interface BookingListState {
  valid: boolean;
  propertyId: string | null;
  status: AdminBookingStatus | null;
  startsFrom: string | null;
  startsBefore: string | null;
  cursor: string | null;
  sortBy: AdminBookingSortField | null;
  sortDirection: AdminBookingSortDirection | null;
}

export function bookingListState(params: URLSearchParams): BookingListState {
  const propertyId = params.get('property_id');
  const rawStatus = params.get('status');
  const startsFrom = params.get('starts_from');
  const startsBefore = params.get('starts_before');
  const cursor = params.get('cursor');
  const rawSortBy = params.get('sort_by');
  const rawSortDirection = params.get('sort_direction');
  const sorted = rawSortBy !== null || rawSortDirection !== null;
  const valid =
    [...params.keys()].every((key) => LIST_KEYS.includes(key as (typeof LIST_KEYS)[number])) &&
    LIST_KEYS.every((key) => params.getAll(key).length <= 1) &&
    (propertyId === null || canonicalUuid(propertyId)) &&
    (rawStatus === null || STATUSES.includes(rawStatus as AdminBookingStatus)) &&
    ((startsFrom === null && startsBefore === null) ||
      (startsFrom !== null &&
        startsBefore !== null &&
        isOffsetDateTime(startsFrom) &&
        isOffsetDateTime(startsBefore) &&
        Date.parse(startsFrom) < Date.parse(startsBefore))) &&
    ((rawSortBy === null && rawSortDirection === null) ||
      (rawSortBy !== null &&
        SORT_FIELDS.includes(rawSortBy as AdminBookingSortField) &&
        (rawSortDirection === 'asc' || rawSortDirection === 'desc'))) &&
    (cursor === null || (cursor.length > 0 && cursor.length <= (sorted ? 1024 : 256)));
  return {
    valid,
    propertyId,
    status: rawStatus as AdminBookingStatus | null,
    startsFrom,
    startsBefore,
    cursor,
    sortBy: rawSortBy as AdminBookingSortField | null,
    sortDirection: rawSortDirection as AdminBookingSortDirection | null,
  };
}

export function bookingReturnTo(value: string | null): string {
  if (
    !value ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    hasAsciiControlOrSpace(value)
  )
    return '/contracts';
  try {
    const url = new URL(value, 'https://internal.invalid');
    if (
      url.origin !== 'https://internal.invalid' ||
      url.pathname !== '/contracts' ||
      url.hash ||
      !bookingListState(url.searchParams).valid
    )
      return '/contracts';
    return url.pathname + url.search;
  } catch {
    return '/contracts';
  }
}

export function bookingDetailState(params: URLSearchParams) {
  const orderId = params.get('order_id');
  const returnTo = params.get('returnTo');
  return {
    valid:
      params.getAll('order_id').length <= 1 &&
      params.getAll('returnTo').length <= 1 &&
      [...params.keys()].every((key) => key === 'order_id' || key === 'returnTo') &&
      (orderId === null || canonicalUuid(orderId)),
    orderId,
    returnTo: bookingReturnTo(returnTo),
  };
}

export function bookingDetailHref(bookingId: string, returnTo: string): string {
  return `/contracts/${bookingId}?${new URLSearchParams({ returnTo: bookingReturnTo(returnTo) })}`;
}
